// src/utils/mockDataRoster.ts
// Allocation engine — links employees & equipment to jobs on specific dates
// Conflict detection prevents double-booking
// Auto-generates roster entries and prefills timesheets

import { MOCK_JOBS } from './mockData'

// ─────────────────────────────────────────────────────────────────
// JOB COLOUR MAP  (for the roster grid — matches PROJECT_TYPE_META)
// ─────────────────────────────────────────────────────────────────
export const JOB_ALLOC_COLOUR: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  j1: { bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300',   dot: 'bg-blue-500' },
  j2: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300', dot: 'bg-indigo-500' },
  j3: { bg: 'bg-teal-100',   text: 'text-teal-800',   border: 'border-teal-300',   dot: 'bg-teal-500' },
  j4: { bg: 'bg-amber-100',  text: 'text-amber-800',  border: 'border-amber-400',  dot: 'bg-amber-500' },
  j5: { bg: 'bg-green-100',  text: 'text-green-800',  border: 'border-green-300',  dot: 'bg-green-500' },
  j6: { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300', dot: 'bg-violet-500' },
  LEAVE:     { bg: 'bg-gray-100',   text: 'text-gray-500',   border: 'border-gray-300',   dot: 'bg-gray-400' },
  CONFLICT:  { bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-400',    dot: 'bg-red-600' },
}

// ─────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────
export type ShiftType = 'DAY' | 'NIGHT' | 'HALF'

export type DayAllocation = {
  id: string
  employeeId: string
  jobId: string
  date: string           // 'YYYY-MM-DD'
  shiftType: ShiftType
  allocatedById: string  // userId
  allocatedByName: string
  allocatedAt: string    // ISO timestamp
  timesheetId: string | null  // set once timesheet entry created
  notes: string | null
}

export type EquipmentDayAllocation = {
  id: string
  equipmentId: string
  jobId: string
  date: string
  allocatedById: string; allocatedByName: string; allocatedAt: string
  notes: string | null
}

export type RosterEntry = {
  employeeId: string; employeeName: string; jobId: string; jobNumber: string
  week: string  // 'YYYY-WNN'
  days: Record<string, { shiftType: ShiftType; allocationId: string } | null>
  // days keys: 'Mon' 'Tue' 'Wed' 'Thu' 'Fri' 'Sat' 'Sun'
}

export type AvailabilityStatus = 'AVAILABLE' | 'ALLOCATED' | 'LEAVE' | 'UNAVAILABLE' | 'CONFLICT'

export type DayAvailability = {
  date: string
  status: AvailabilityStatus
  jobId?: string
  jobNumber?: string
  shiftType?: ShiftType
  allocationId?: string
}

// ─────────────────────────────────────────────────────────────────
// CURRENT WEEK DATES  (week of Mon 16 Jun 2026)
// ─────────────────────────────────────────────────────────────────
export const WEEK_START  = '2026-06-15'
export const WEEK_DAYS   = ['2026-06-15','2026-06-16','2026-06-17','2026-06-18','2026-06-19'] // Mon-Fri
export const DAY_LABELS  = ['Mon','Tue','Wed','Thu','Fri']
export const PREV_WEEK_DAYS = ['2026-06-08','2026-06-09','2026-06-10','2026-06-11','2026-06-12']

// ─────────────────────────────────────────────────────────────────
// EMPLOYEE ALLOCATIONS  (current week + prior week)
// ─────────────────────────────────────────────────────────────────
export const MOCK_ALLOCATIONS: DayAllocation[] = [
  // ── Current week (16-20 Jun) ──────────────────────────────────

  // e1 Tony Rivera → j1 Kessels Rd all week
  { id: 'al001', employeeId: 'e1', jobId: 'j1', date: '2026-06-15', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:00:00Z', timesheetId: 'ts001', notes: null },
  { id: 'al002', employeeId: 'e1', jobId: 'j1', date: '2026-06-16', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:00:00Z', timesheetId: 'ts001', notes: null },
  { id: 'al003', employeeId: 'e1', jobId: 'j1', date: '2026-06-17', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:00:00Z', timesheetId: 'ts001', notes: null },
  { id: 'al004', employeeId: 'e1', jobId: 'j1', date: '2026-06-18', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:00:00Z', timesheetId: 'ts001', notes: null },
  { id: 'al005', employeeId: 'e1', jobId: 'j1', date: '2026-06-19', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:00:00Z', timesheetId: 'ts001', notes: null },

  // e2 Lisa Grant → j1 Mon-Wed, j5 Thu-Fri (split week)
  { id: 'al006', employeeId: 'e2', jobId: 'j1', date: '2026-06-15', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:05:00Z', timesheetId: 'ts002', notes: null },
  { id: 'al007', employeeId: 'e2', jobId: 'j1', date: '2026-06-16', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:05:00Z', timesheetId: 'ts002', notes: null },
  { id: 'al008', employeeId: 'e2', jobId: 'j1', date: '2026-06-17', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:05:00Z', timesheetId: 'ts002', notes: null },
  { id: 'al009', employeeId: 'e2', jobId: 'j5', date: '2026-06-18', shiftType: 'DAY', allocatedById: 'u5', allocatedByName: 'Mike Walsh',  allocatedAt: '2026-06-12T10:00:00Z', timesheetId: 'ts003', notes: 'TC coverage for Ipswich Rd defect inspection' },
  { id: 'al010', employeeId: 'e2', jobId: 'j5', date: '2026-06-19', shiftType: 'DAY', allocatedById: 'u5', allocatedByName: 'Mike Walsh',  allocatedAt: '2026-06-12T10:00:00Z', timesheetId: 'ts003', notes: null },


  // e5 Karen Patel → j1 Mon-Thu (traffic control)
  { id: 'al021', employeeId: 'e5', jobId: 'j1', date: '2026-06-15', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:15:00Z', timesheetId: 'ts006', notes: null },
  { id: 'al022', employeeId: 'e5', jobId: 'j1', date: '2026-06-16', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:15:00Z', timesheetId: 'ts006', notes: null },
  { id: 'al023', employeeId: 'e5', jobId: 'j1', date: '2026-06-17', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:15:00Z', timesheetId: 'ts006', notes: null },
  { id: 'al024', employeeId: 'e5', jobId: 'j1', date: '2026-06-18', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:15:00Z', timesheetId: 'ts006', notes: null },
  // Karen free on Fri

  // e6 Dave Fielding → j3 Gold Coast Thu-Fri (starts Broadbeach assignment)
  { id: 'al025', employeeId: 'e6', jobId: 'j3', date: '2026-06-18', shiftType: 'DAY', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-15T10:00:00Z', timesheetId: null, notes: 'FM flag: AMBER — accommodation at Quest Broadbeach' },
  { id: 'al026', employeeId: 'e6', jobId: 'j3', date: '2026-06-19', shiftType: 'DAY', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-15T10:00:00Z', timesheetId: null, notes: null },

  // e7 Mike Torres → j2 Pacific Mwy all week (Day 5 of 6 — YELLOW FM flag)
  { id: 'al027', employeeId: 'e7', jobId: 'j2', date: '2026-06-15', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:20:00Z', timesheetId: 'ts007', notes: null },
  { id: 'al028', employeeId: 'e7', jobId: 'j2', date: '2026-06-16', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:20:00Z', timesheetId: 'ts007', notes: null },
  { id: 'al029', employeeId: 'e7', jobId: 'j2', date: '2026-06-17', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:20:00Z', timesheetId: 'ts007', notes: null },
  { id: 'al030', employeeId: 'e7', jobId: 'j2', date: '2026-06-18', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:20:00Z', timesheetId: 'ts007', notes: null },
  { id: 'al031', employeeId: 'e7', jobId: 'j2', date: '2026-06-19', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-17T09:40:00Z', timesheetId: 'ts007', notes: 'Day 5 — YELLOW FM flag, rest day Sat mandatory' },

  // e8 Sarah Kim → j2 Mon-Wed (commissioning prep)
  { id: 'al032', employeeId: 'e8', jobId: 'j2', date: '2026-06-15', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:25:00Z', timesheetId: 'ts008', notes: null },
  { id: 'al033', employeeId: 'e8', jobId: 'j2', date: '2026-06-16', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:25:00Z', timesheetId: 'ts008', notes: null },
  { id: 'al034', employeeId: 'e8', jobId: 'j2', date: '2026-06-17', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:25:00Z', timesheetId: 'ts008', notes: null },
  // Sarah free Thu-Fri

  // ── Previous week (9-13 Jun — historical, for payroll demo) ───
  { id: 'al100', employeeId: 'e1', jobId: 'j1', date: '2026-06-08', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts101', notes: null },
  { id: 'al101', employeeId: 'e1', jobId: 'j1', date: '2026-06-09', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts101', notes: null },
  { id: 'al102', employeeId: 'e1', jobId: 'j1', date: '2026-06-10', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts101', notes: null },
  { id: 'al103', employeeId: 'e1', jobId: 'j1', date: '2026-06-11', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts101', notes: null },
  { id: 'al104', employeeId: 'e1', jobId: 'j1', date: '2026-06-12', shiftType: 'DAY', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts101', notes: null },
  { id: 'al110', employeeId: 'e3', jobId: 'j4', date: '2026-06-08', shiftType: 'DAY', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts104', notes: null },
  { id: 'al111', employeeId: 'e3', jobId: 'j4', date: '2026-06-09', shiftType: 'DAY', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts104', notes: null },
  { id: 'al112', employeeId: 'e3', jobId: 'j4', date: '2026-06-10', shiftType: 'DAY', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts104', notes: null },
  { id: 'al113', employeeId: 'e3', jobId: 'j4', date: '2026-06-11', shiftType: 'DAY', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts104', notes: null },
  { id: 'al114', employeeId: 'e3', jobId: 'j4', date: '2026-06-12', shiftType: 'DAY', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-05T09:00:00Z', timesheetId: 'ts104', notes: null },
]

// ─────────────────────────────────────────────────────────────────
// EQUIPMENT ALLOCATIONS
// ─────────────────────────────────────────────────────────────────
export const MOCK_EQUIPMENT_ALLOCATIONS: EquipmentDayAllocation[] = [
  // PLT-001 Kubota Excavator → j4 all week
  { id: 'ea001', equipmentId: 'eq1', jobId: 'j4', date: '2026-06-15', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:00:00Z', notes: null },
  { id: 'ea002', equipmentId: 'eq1', jobId: 'j4', date: '2026-06-16', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:00:00Z', notes: null },
  { id: 'ea003', equipmentId: 'eq1', jobId: 'j4', date: '2026-06-17', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:00:00Z', notes: null },
  { id: 'ea004', equipmentId: 'eq1', jobId: 'j4', date: '2026-06-18', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:00:00Z', notes: null },
  { id: 'ea005', equipmentId: 'eq1', jobId: 'j4', date: '2026-06-19', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:00:00Z', notes: null },
  // PLT-002 Snorkel EWP → j5 all week
  { id: 'ea006', equipmentId: 'eq2', jobId: 'j5', date: '2026-06-15', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:05:00Z', notes: null },
  { id: 'ea007', equipmentId: 'eq2', jobId: 'j5', date: '2026-06-16', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:05:00Z', notes: null },
  { id: 'ea008', equipmentId: 'eq2', jobId: 'j5', date: '2026-06-17', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:05:00Z', notes: null },
  { id: 'ea009', equipmentId: 'eq2', jobId: 'j5', date: '2026-06-18', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:05:00Z', notes: null },
  { id: 'ea010', equipmentId: 'eq2', jobId: 'j5', date: '2026-06-19', allocatedById: 'u5', allocatedByName: 'Mike Walsh', allocatedAt: '2026-06-12T09:05:00Z', notes: null },
  // VEH-001 Ford Ranger → j1 Mon-Thu
  { id: 'ea011', equipmentId: 'eq5', jobId: 'j1', date: '2026-06-15', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:10:00Z', notes: null },
  { id: 'ea012', equipmentId: 'eq5', jobId: 'j1', date: '2026-06-16', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:10:00Z', notes: null },
  { id: 'ea013', equipmentId: 'eq5', jobId: 'j1', date: '2026-06-17', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:10:00Z', notes: null },
  { id: 'ea014', equipmentId: 'eq5', jobId: 'j1', date: '2026-06-18', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:10:00Z', notes: null },
  // VEH-002 Toyota Hilux → j2 all week
  { id: 'ea015', equipmentId: 'eq6', jobId: 'j2', date: '2026-06-15', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:15:00Z', notes: null },
  { id: 'ea016', equipmentId: 'eq6', jobId: 'j2', date: '2026-06-16', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:15:00Z', notes: null },
  { id: 'ea017', equipmentId: 'eq6', jobId: 'j2', date: '2026-06-17', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:15:00Z', notes: null },
  { id: 'ea018', equipmentId: 'eq6', jobId: 'j2', date: '2026-06-18', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:15:00Z', notes: null },
  { id: 'ea019', equipmentId: 'eq6', jobId: 'j2', date: '2026-06-19', allocatedById: 'u4', allocatedByName: 'Sarah Lee', allocatedAt: '2026-06-12T09:15:00Z', notes: null },
]

// ─────────────────────────────────────────────────────────────────
// CANONICAL SHIFT CONSTANTS
// These are the platform-wide defaults and feed into:
//   • Roster display (which columns are active/greyed)
//   • Conflict detection (same shift type only)
//   • Timesheet autofill (hours per shift, valid days)
//   • Payroll / PAYG / superannuation calculations
// ─────────────────────────────────────────────────────────────────

/** Day-of-week integers: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat */
export const DAY_SHIFT_WORK_DAYS:  number[] = [1, 2, 3, 4, 5]   // Mon–Fri
export const NIGHT_SHIFT_WORK_DAYS: number[] = [0, 1, 2, 3, 4]  // Sun–Thu

export const DAY_SHIFT_OFF_DAYS:   number[] = [0, 6]   // Sun, Sat
export const NIGHT_SHIFT_OFF_DAYS: number[] = [5, 6]   // Fri, Sat

/** Standard ordinary hours per shift for payroll calculations */
export const SHIFT_HOURS = {
  DAY:   8,   // 8h ordinary day shift
  NIGHT: 9,   // 9h night shift (typically longer + night loading)
  HALF:  4,   // 4h half-day / call-out
} as const

/** Human-readable shift labels */
export const DAY_SHIFT_LABEL   = 'Day shift — Monday to Friday'
export const NIGHT_SHIFT_LABEL = 'Night shift — Sunday to Thursday (evening)'

/** Returns true when the given date is a valid working day for the shift type */
export function isValidShiftDay(date: string, shiftType: 'DAY' | 'NIGHT'): boolean {
  const dow = new Date(date + 'T00:00:00').getDay()
  return shiftType === 'DAY'
    ? DAY_SHIFT_WORK_DAYS.includes(dow)
    : NIGHT_SHIFT_WORK_DAYS.includes(dow)
}

/** Returns true when the given date is an off-shift day (greyed in grid) */
export function isOffShiftDay(date: string, shiftView: 'DAY' | 'NIGHT'): boolean {
  const dow = new Date(date + 'T00:00:00').getDay()
  return shiftView === 'DAY'
    ? DAY_SHIFT_OFF_DAYS.includes(dow)
    : NIGHT_SHIFT_OFF_DAYS.includes(dow)
}

// ─────────────────────────────────────────────────────────────────
// ALLOCATION ENGINE HELPERS
// ─────────────────────────────────────────────────────────────────

/**
 * Get allocation status for an employee on a specific date.
 * When shiftType is provided, only allocations of that type are considered.
 * This prevents NIGHT allocations from appearing as "allocated" in DAY view.
 */
export function getEmployeeDayStatus(
  employeeId: string,
  date: string,
  allocations: DayAllocation[] = MOCK_ALLOCATIONS,
  shiftType?: 'DAY' | 'NIGHT',
): DayAvailability {
  const alloc = allocations.find(
    a => a.employeeId === employeeId &&
         a.date === date &&
         (!shiftType || a.shiftType === shiftType)
  )
  if (!alloc) return { date, status: 'AVAILABLE' }

  const job = MOCK_JOBS.find(j => j.id === alloc.jobId)
  return {
    date,
    status: 'ALLOCATED',
    jobId: alloc.jobId,
    jobNumber: job?.jobNumber ?? alloc.jobId,
    shiftType: alloc.shiftType,
    allocationId: alloc.id,
  }
}

/**
 * Get full week availability for an employee.
 * Pass shiftType to filter by DAY or NIGHT — prevents cross-shift
 * availability pollution (night allocs counting in day view).
 */
export function getEmployeeWeekAvailability(
  employeeId: string,
  weekDates: string[] = WEEK_DAYS,
  allocations: DayAllocation[] = MOCK_ALLOCATIONS,
  shiftType?: 'DAY' | 'NIGHT',
): DayAvailability[] {
  return weekDates.map(date => getEmployeeDayStatus(employeeId, date, allocations, shiftType))
}

/**
 * Check for a scheduling conflict when attempting a new allocation.
 * When shiftType is provided, only conflicts within the same shift type are
 * reported — a NIGHT allocation on Monday does not block a DAY allocation
 * on Monday (they are separate rosters). An attempt to add a SECOND
 * allocation of the SAME shift type on the same date is always a conflict.
 */
export function checkAllocationConflict(
  employeeId: string,
  dates: string[],
  excludeAllocationId?: string,
  allocations: DayAllocation[] = MOCK_ALLOCATIONS,
  shiftType?: 'DAY' | 'NIGHT' | 'HALF',
): { hasConflict: boolean; conflictDate?: string; conflictJobId?: string; conflictJobNumber?: string } {
  for (const date of dates) {
    const existing = allocations.find(
      a => a.employeeId === employeeId &&
           a.date === date &&
           a.id !== excludeAllocationId &&
           (!shiftType || a.shiftType === shiftType)  // same shift type only
    )
    if (existing) {
      const job = MOCK_JOBS.find(j => j.id === existing.jobId)
      return {
        hasConflict: true,
        conflictDate: date,
        conflictJobId: existing.jobId,
        conflictJobNumber: job?.jobNumber ?? existing.jobId,
      }
    }
  }
  return { hasConflict: false }
}

/** Check for equipment allocation conflict */
export function checkEquipmentConflict(
  equipmentId: string,
  dates: string[],
  excludeId?: string,
  allocations: EquipmentDayAllocation[] = MOCK_EQUIPMENT_ALLOCATIONS,
): { hasConflict: boolean; conflictDate?: string; conflictJobId?: string } {
  for (const date of dates) {
    const existing = allocations.find(
      a => a.equipmentId === equipmentId && a.date === date && a.id !== excludeId
    )
    if (existing) return { hasConflict: true, conflictDate: date, conflictJobId: existing.jobId }
  }
  return { hasConflict: false }
}

/** How many days is an employee allocated this week */
export function employeeWeekDayCount(
  employeeId: string,
  weekDates: string[] = WEEK_DAYS,
  allocations: DayAllocation[] = MOCK_ALLOCATIONS,
): number {
  return allocations.filter(a => a.employeeId === employeeId && weekDates.includes(a.date)).length
}

/** Get all employees allocated to a job on a given week */
export function getJobWeekCrew(
  jobId: string,
  weekDates: string[] = WEEK_DAYS,
  allocations: DayAllocation[] = MOCK_ALLOCATIONS,
): string[] {
  const ids = new Set(
    allocations.filter(a => a.jobId === jobId && weekDates.includes(a.date)).map(a => a.employeeId)
  )
  return [...ids]
}

/** Summary of total day-allocations for payroll (employee × job × week) */
export type PayrollLine = {
  employeeId: string; employeeName: string; jobId: string; jobNumber: string
  daysWorked: number; hoursWorked: number; shiftBreakdown: Record<string, number>
  weekStart: string
}

export function generatePayrollSummary(
  weekDates: string[],
  allocations: DayAllocation[] = MOCK_ALLOCATIONS,
  employeeNames: Record<string, string>,
): PayrollLine[] {
  const map = new Map<string, PayrollLine>()

  allocations
    .filter(a => weekDates.includes(a.date))
    .forEach(a => {
      const key = `${a.employeeId}:${a.jobId}`
      const job = MOCK_JOBS.find(j => j.id === a.jobId)
      if (!map.has(key)) {
        map.set(key, {
          employeeId: a.employeeId,
          employeeName: employeeNames[a.employeeId] ?? a.employeeId,
          jobId: a.jobId,
          jobNumber: job?.jobNumber ?? a.jobId,
          daysWorked: 0,
          hoursWorked: 0,
          shiftBreakdown: { DAY: 0, NIGHT: 0, HALF: 0 },
          weekStart: weekDates[0],
        })
      }
      const line = map.get(key)!
      line.daysWorked++
      // Shift-specific hours for payroll / PAYG / super calculation
      line.hoursWorked += SHIFT_HOURS[a.shiftType as keyof typeof SHIFT_HOURS] ?? SHIFT_HOURS.DAY
      line.shiftBreakdown[a.shiftType] = (line.shiftBreakdown[a.shiftType] ?? 0) + 1
    })

  return [...map.values()].sort((a, b) => a.employeeId.localeCompare(b.employeeId))
}
